import TableRowsIcon from "@mui/icons-material/TableRows";
import { useSnackbar } from "notistack";
import type { FC } from "react";
import { useMemo } from "react";
import DownloadFileButton from "../../components/downloadFileButton/DownloadFileButton";
import type { DownloadOption } from "../../components/downloadFileButton/MultiOptionDownloadButton";
import { getParticipantListVersionSpreadsheet, getParticipantListVersionZip } from "./participantListApi";

export interface SpreadsheetExportTargetOption {
  readonly providerName: string;
  readonly bookingNumber: string;
}

interface Props {
  readonly buttonText: string;
  readonly orderId: string;
  readonly listVersionNumber: number;
  readonly userFriendlyOrderId: number;
  readonly spreadsheetExportTargets: SpreadsheetExportTargetOption[];
}

const DownloadParticipantListVersionButton: FC<Props> = ({
  buttonText,
  orderId,
  listVersionNumber,
  userFriendlyOrderId,
  spreadsheetExportTargets,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const zipFilename = `${userFriendlyOrderId} - lista (wersja ${listVersionNumber}).zip`;

  const options: DownloadOption[] = useMemo(() => {
    const downloadZip = () => getParticipantListVersionZip(orderId, listVersionNumber);
    return [
      { name: buttonText, apiDefinition: downloadZip },
      ...spreadsheetExportTargets.map((t) => ({
        name: `${t.providerName} (${t.bookingNumber})`,
        apiDefinition: () => getParticipantListVersionSpreadsheet(orderId, listVersionNumber, t.bookingNumber),
      })),
    ];
  }, [buttonText, listVersionNumber, orderId, spreadsheetExportTargets]);

  const filename: string | ((key?: string) => string) = useMemo(() => {
    return (key?: string) => {
      if (key === buttonText || key === undefined) {
        return zipFilename;
      }
      const t = spreadsheetExportTargets.find((x) => `${x.providerName} (${x.bookingNumber})` === key);
      return t ? `${userFriendlyOrderId} - ${t.providerName} (${t.bookingNumber}).xlsx` : zipFilename;
    };
  }, [buttonText, spreadsheetExportTargets, userFriendlyOrderId, zipFilename]);

  return (
    <DownloadFileButton
      startIcon={<TableRowsIcon />}
      buttonText={buttonText}
      options={options}
      filename={filename}
      primaryOption
      onError={() =>
        enqueueSnackbar("Nie udało się pobrać pliku", {
          variant: "error",
        })
      }
    />
  );
};

export default DownloadParticipantListVersionButton;
